import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { frontMiddleBack } from "../lib/greenFMB";
import WatchWindChip from "../components/WatchWindChip";

/**
 * Watch companion page. Designed for tiny smartwatch browsers (Galaxy Watch /
 * Wear OS — Samsung Internet works) but also great on a phone for one-handed
 * play. Auth via watch_token in the URL — no JWT/login.
 *
 * URL: /watch?r=ROUND_ID&t=WATCH_TOKEN
 */
const POLL_MS = 5000;
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function WatchView() {
  const params = new URLSearchParams(window.location.search);
  const [roundId, setRoundId] = useState(params.get("r") || localStorage.getItem("fairwayWatchRoundId") || "");
  const [token, setToken] = useState(params.get("t") || localStorage.getItem("fairwayWatchToken") || "");

  const [state, setState] = useState(null);
  const [err, setErr] = useState("");
  const [started, setStarted] = useState(false);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoSwing, setAutoSwing] = useState(false);
  // GPS-based auto-arm. When the player walks within ~30 yds of the card
  // distance to the pin (i.e. they've arrived at the tee), the watch auto-
  // taps "Start Hole" for them. Default ON; persisted in localStorage.
  const [autoArm, setAutoArm] = useState(
    () => localStorage.getItem("fairwayWatchAutoArm") !== "false"
  );
  const [swingHint, setSwingHint] = useState("");
  // Summary screen — populated when the user finishes the round on the watch
  const [summary, setSummary] = useState(null);
  const [finishBusy, setFinishBusy] = useState(false);
  // Garmin-face secondary menu (auto-arm toggle, prev hole, finish round,
  // home). Triggered by tapping the face or the ☰ button.
  const [menuOpen, setMenuOpen] = useState(false);

  // Wind state bubbled up from WatchWindChip so we can render a "play it
  // as N" yardage hint under the headline middle yardage.
  const [wind, setWind] = useState(null);

  // Screen Wake Lock — keep the watch screen on during active play so you
  // don't have to keep waking the device between shots. Released
  // automatically on tab hide / unmount. Best-effort: silently no-op on
  // browsers that don't support it (older Safari pre-iOS 16.4).
  useEffect(() => {
    if (!started || !state || state.completed) return;
    let lock = null;
    let cancelled = false;
    const request = async () => {
      try {
        if ("wakeLock" in navigator) {
          lock = await navigator.wakeLock.request("screen");
          // If the lock auto-releases when the user backgrounds the tab,
          // try to re-acquire when they return.
          lock.addEventListener?.("release", () => { lock = null; });
        }
      } catch (e) {
        // Permission denied / unsupported — silent fallback.
        console.warn("Watch: wake-lock unavailable", e);
      }
    };
    request();
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !lock && !cancelled) request();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (lock) { try { lock.release(); } catch (_e) { /* no-op */ } }
    };
  }, [started, state]);
  // Login state
  const [loginEmail, setLoginEmail] = useState(localStorage.getItem("fairwayWatchEmail") || "");
  const [loginPin, setLoginPin] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loggedInName, setLoggedInName] = useState(localStorage.getItem("fairwayWatchName") || "");
  // Course-picker state for the "Start round from watch" flow
  const [pickerCourses, setPickerCourses] = useState(null); // null = not yet fetched, [] = none
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerErr, setPickerErr] = useState("");
  const watchIdRef = useRef(null);
  const lastSwingRef = useRef(0);
  const sendEventRef = useRef(null);
  const holeArmedRef = useRef(false);
  const autoArmDwellRef = useRef(null);
  // PWA install — for adding the watch app to the home screen so users
  // don't have to open Samsung Internet every round.
  const installPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);

  // Auto-open the install help on first visit for browsers that don't
  // support beforeinstallprompt (Samsung Internet on watch, Safari, etc.)
  // — so Galaxy Watch / Apple Watch users see the right tap-by-tap steps
  // without hunting for the button. Fires after a 3s grace period to let
  // beforeinstallprompt arrive on browsers that support it.
  useEffect(() => {
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    if (isStandalone) return;
    if (localStorage.getItem("fairwayWatchInstallHelpSeen") === "1") return;
    const t = setTimeout(() => {
      if (!installPromptRef.current && !isStandalone) {
        setShowInstallHelp(true);
      }
    }, 3000);
    return () => clearTimeout(t);
  }, []);
  // Sync indicator — updates whenever a watch poll succeeds. The play view
  // shows "Xs ago" so the user knows the watch is talking to the server.
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [, setNowTick] = useState(0);
  // Offline event queue — taps that didn't make it to the server (no signal,
  // server unreachable, etc.) get parked in localStorage and replayed when
  // connectivity returns. No cap; events replay in order.
  const QUEUE_KEY = "fairwayWatchOfflineQueue";
  const loadQueue = () => {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };
  const saveQueue = (q) => {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    } catch {}
  };
  const [pendingCount, setPendingCount] = useState(loadQueue().length);
  const drainingRef = useRef(false);

  // Crowdsourced pin confirmation from the watch. Shows a "Confirm pin" pill
  // on the face when the player is within 30 yds of the pin — one tap POSTs
  // their GPS to /watch/round/{id}/confirm-pin and shows a short "N of 3"
  // ack. Piggy-backs on the same 3-player clustering the phone uses.
  const [pinConfirm, setPinConfirm] = useState({ busy: false, ack: null, err: null });
  const pinConfirmAckTimerRef = useRef(null);

  // Track network status so we can show an "OFFLINE — using last known data"
  // chip on the watch face. The service worker keeps serving cached state
  // when the server is unreachable, so the face still works — but the user
  // needs to KNOW that's what's happening (distances may be a few seconds
  // stale, score-saves are queued locally).
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Hide the "Made with Emergent" branding badge while the watch UI is
  // mounted — on a small round watch face it covers controls. Restored on
  // unmount so the rest of the app keeps it. The badge lives in index.html
  // (#emergent-badge) so we just toggle inline display.
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    const prev = badge?.style.display;
    if (badge) badge.style.display = "none";
    return () => {
      if (badge) badge.style.display = prev || "";
    };
  }, []);

  // Swap the global <link rel="manifest"> to the watch-specific manifest so
  // "Add to home screen" creates a shortcut that opens at /watch directly
  // (not the main /app dashboard). Also detect whether we're already running
  // in standalone mode (which means it's already installed).
  useEffect(() => {
    const links = document.querySelectorAll('link[rel="manifest"]');
    const previous = links.length > 0 ? links[0].getAttribute("href") : "/manifest.json";
    links.forEach((l) => l.setAttribute("href", "/watch-manifest.json"));
    const standalone =
      window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    setIsInstalled(!!standalone || window.navigator.standalone === true);
    return () => {
      // Restore the original manifest when leaving the watch page
      const links2 = document.querySelectorAll('link[rel="manifest"]');
      links2.forEach((l) => l.setAttribute("href", previous));
    };
  }, []);

  useEffect(() => {
    const beforeInstall = (e) => {
      e.preventDefault();
      installPromptRef.current = e;
      setCanInstall(true);
    };
    const installed = () => {
      installPromptRef.current = null;
      setCanInstall(false);
      setIsInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const doInstall = async ({ silent = false } = {}) => {
    if (!installPromptRef.current) {
      // Browser doesn't support automatic install (e.g. Safari, some watch
      // browsers) — show the manual "Add to home screen" instructions, but
      // only when triggered by an explicit user tap. Silent (auto) calls
      // skip the modal so first-time users aren't pelted with a wall of
      // help text on their watch.
      if (!silent) setShowInstallHelp(true);
      return;
    }
    try {
      installPromptRef.current.prompt();
      const choice = await installPromptRef.current.userChoice;
      if (choice && choice.outcome === "accepted") {
        setCanInstall(false);
      }
      // Either way (accepted or dismissed), consume the prompt — browsers
      // won't fire beforeinstallprompt again until the user clears caches.
      installPromptRef.current = null;
    } catch {
      setShowInstallHelp(true);
    }
  };

  // GPS — high-accuracy, watch one position so battery stays sane.
  // When the host phone is broadcasting its position (`state.position_source
  // === "phone"`), we throttle the watch's own GPS hard — phone has a much
  // stronger antenna, and continuous watchPosition() on a smartwatch is a
  // battery killer. We still keep a slow watch alive so we can fall back if
  // the phone goes offline mid-round.
  const phoneIsSource = state?.position_source === "phone";
  useEffect(() => {
    if (!navigator.geolocation) return;
    const opts = phoneIsSource
      // Phone is the source — only refresh GPS every 30s, accept fixes up
      // to 60s old, no urgency. Saves watch battery massively.
      ? { enableHighAccuracy: false, maximumAge: 60_000, timeout: 30_000 }
      // Watch is the source — high accuracy, low staleness.
      : { enableHighAccuracy: true, maximumAge: 6000, timeout: 15000 };
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      opts,
    );
    return () => navigator.geolocation.clearWatch(watchIdRef.current);
  }, [phoneIsSource]);

  // Auto swing detection — listens for >35 m/s² acceleration spikes with a
  // 1.5s cooldown. Each detected swing auto-fires a "stroke" event.
  useEffect(() => {
    if (!autoSwing) return;
    const THRESHOLD = 35;
    const COOLDOWN_MS = 1500;
    const handler = (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      if (mag > THRESHOLD && Date.now() - lastSwingRef.current > COOLDOWN_MS) {
        lastSwingRef.current = Date.now();
        // Don't fire auto-strokes until the user has tapped Start Hole —
        // practice swings on the tee must not count.
        if (!holeArmedRef.current) {
          setSwingHint("● Practice — tap Start Hole");
          setTimeout(() => setSwingHint(""), 1500);
          return;
        }
        setSwingHint("● Swing detected");
        setTimeout(() => setSwingHint(""), 1200);
        sendEventRef.current?.("stroke");
      }
    };
    window.addEventListener("devicemotion", handler);
    return () => window.removeEventListener("devicemotion", handler);
  }, [autoSwing]);

  // Clears the stale round+token from state and localStorage so the user is
  // routed back to the PIN login screen. Called whenever the backend tells
  // us the watch token is no longer valid (round finished, token rotated,
  // user logged out from the phone, etc.).
  const clearStaleSession = (msg = "Round expired — sign in again") => {
    try {
      localStorage.removeItem("fairwayWatchRoundId");
      localStorage.removeItem("fairwayWatchToken");
    } catch { /* private mode — ignore */ }
    setRoundId("");
    setToken("");
    setStarted(false);
    setState(null);
    setErr(msg);
    // Also surface the message on the PIN login screen so the user
    // understands why they were bounced back — without this they'd just
    // see a blank login form and not know what happened.
    setLoginErr(msg);
  };

  const refresh = async () => {
    try {
      const qs = new URLSearchParams({ t: token });
      if (pos) {
        qs.set("lat", String(pos.lat));
        qs.set("lng", String(pos.lng));
      }
      const { data } = await axios.get(`${API}/watch/round/${roundId}?${qs.toString()}`);
      // If we still have unsynced taps queued, don't overwrite local state —
      // the server is behind us. Keep the optimistic counts visible until the
      // drain finishes, then we'll refresh again.
      if (loadQueue().length === 0) {
        setState(data);
      }
      setLastSyncAt(Date.now());
      setErr("");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        clearStaleSession();
        return;
      }
      setErr(e?.response?.data?.detail || "Connection lost");
    }
  };

  // Re-render once a second so the "Xs ago" sync label stays current
  // without re-polling the backend.
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Server-Sent Events: the backend pushes a fresh state every ~1.5s (and
  // only when something actually changed). Replaces the old 5s setInterval
  // poll loop. EventSource auto-reconnects on transient errors. We keep a
  // slow 20s HTTP fallback for the watch-position-stale case + as a safety
  // net for proxies that buffer SSE.
  const esRef = useRef(null);
  useEffect(() => {
    if (!started || !roundId || !token) return;
    // Initial state fetch so the watch face has something to render before
    // the first SSE frame arrives.
    refresh();

    let cancelled = false;
    const open = () => {
      if (cancelled) return;
      try {
        const url = `${API}/watch/round/${roundId}/stream?t=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;
        es.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (loadQueue().length === 0) setState(data);
            setLastSyncAt(Date.now());
            setErr("");
          } catch {
            /* ignore malformed frame */
          }
        };
        es.addEventListener("closed", () => {
          // Server told us the token is dead — don't auto-reconnect; bounce
          // the user back to the PIN login screen with a friendly message
          // so they aren't stuck on an "invalid watch token" error.
          es.close();
          esRef.current = null;
          clearStaleSession();
        });
        es.onerror = () => {
          // EventSource will auto-reconnect, but surface a soft "Connecting…"
          // hint if the connection drops for more than a moment.
          if (es.readyState === EventSource.CLOSED) {
            esRef.current = null;
          }
        };
      } catch {
        // Browsers without EventSource — fall back to HTTP polling.
        const id = setInterval(() => {
          if (loadQueue().length > 0) drainQueue(); else refresh();
        }, POLL_MS);
        esRef.current = { close: () => clearInterval(id) };
      }
    };
    open();

    // Slow safety-net poll: every 20s, fire one HTTP refresh so even if SSE
    // gets buffered by an aggressive proxy the watch eventually catches up.
    // Also drains any queued offline taps.
    const fallbackId = setInterval(() => {
      if (loadQueue().length > 0) drainQueue();
      else if (Date.now() - lastSyncAtRef.current > 10_000) refresh();
    }, 20_000);

    return () => {
      cancelled = true;
      if (esRef.current) {
        try { esRef.current.close(); } catch {}
        esRef.current = null;
      }
      clearInterval(fallbackId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, roundId, token]);

  // Keep a ref of the last sync timestamp so the SSE fallback poll above
  // can read it without becoming a dependency (avoids re-opening the
  // EventSource on every sync).
  const lastSyncAtRef = useRef(0);
  useEffect(() => { lastSyncAtRef.current = lastSyncAt; }, [lastSyncAt]);

  // Watch broadcasts its own GPS to the backend every ~5s so the SSE
  // stream (which can't accept per-frame lat/lng) has fresh coords to
  // compute distance from. Skipped when the host phone is the GPS source
  // (phone-position takes priority server-side, and the watch's GPS loop
  // is already throttled hard in that mode).
  const lastWatchPosPostRef = useRef(0);
  useEffect(() => {
    if (!started || !roundId || !token || !pos) return;
    if (phoneIsSource) return;
    const now = Date.now();
    if (now - lastWatchPosPostRef.current < 5000) return;
    lastWatchPosPostRef.current = now;
    axios.post(
      `${API}/watch/round/${roundId}/watch-position?t=${encodeURIComponent(token)}`,
      { lat: pos.lat, lng: pos.lng },
    ).catch(() => { /* best-effort — SSE fallback will reuse last fix */ });
  }, [pos?.lat, pos?.lng, started, roundId, token, phoneIsSource]);

  // When the browser detects we're back online, immediately drain any
  // queued taps that built up while offline.
  useEffect(() => {
    const onOnline = () => {
      if (loadQueue().length > 0) drainQueue();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId, token]);

  const sendEvent = async (type) => {
    if (busy) return;
    setBusy(true);
    // Optimistic local bump for instant feedback on tiny watch screens
    setState((s) => {
      if (!s) return s;
      // Gate strokes and putts behind the per-hole "armed" flag — practice
      // swings before tapping Start Hole must not count.
      if ((type === "stroke" || type === "putt") && !s.hole_armed) return s;
      if (type === "start_hole") return { ...s, hole_armed: true, strokes: 0, putts: null };
      if (type === "stroke") return { ...s, strokes: (s.strokes || 0) + 1 };
      if (type === "putt") return { ...s, strokes: (s.strokes || 0) + 1, putts: (s.putts || 0) + 1 };
      if (type === "undo_stroke") return { ...s, strokes: Math.max(0, (s.strokes || 0) - 1) };
      return s;
    });
    // Always enqueue first; drainQueue handles fast-path (online → POST
    // immediately) and offline-path (leave it queued, retry on next sync).
    const q = loadQueue();
    q.push({ type, at: Date.now() });
    saveQueue(q);
    setPendingCount(q.length);
    await drainQueue();
    setBusy(false);
  };

  // Crowdsource the pin location — one tap on the "Confirm pin" pill when
  // near the green. Shared with the phone via the same backend cluster, so
  // a mix of watch + phone confirmations trigger the 3-player promotion.
  const confirmPin = async () => {
    if (pinConfirm.busy) return;
    if (!pos || pos.lat == null || pos.lng == null) {
      setPinConfirm({ busy: false, ack: null, err: "No GPS" });
      if (pinConfirmAckTimerRef.current) clearTimeout(pinConfirmAckTimerRef.current);
      pinConfirmAckTimerRef.current = setTimeout(
        () => setPinConfirm({ busy: false, ack: null, err: null }),
        2000,
      );
      return;
    }
    setPinConfirm({ busy: true, ack: null, err: null });
    try {
      const { data } = await axios.post(
        `${API}/watch/round/${roundId}/confirm-pin?t=${encodeURIComponent(token)}`,
        { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy ?? null },
      );
      setPinConfirm({
        busy: false,
        ack: data.locked
          ? "Pin locked in ✓"
          : `Confirmed · ${data.confirmations} of 3`,
        err: null,
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail
        || (e?.response?.status === 400 ? "GPS not sharp enough" : "Try again");
      setPinConfirm({ busy: false, ack: null, err: msg });
    }
    if (pinConfirmAckTimerRef.current) clearTimeout(pinConfirmAckTimerRef.current);
    pinConfirmAckTimerRef.current = setTimeout(
      () => setPinConfirm({ busy: false, ack: null, err: null }),
      2500,
    );
  };

  // Drain queue: try each pending event in order. On success → pop & continue.
  // On failure → leave the rest in the queue and stop. Refreshes state once at
  // the end (so a refresh doesn't overwrite our optimistic counts while there
  // are still pending writes).
  const drainQueue = async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      let q = loadQueue();
      while (q.length > 0) {
        const ev = q[0];
        try {
          const { data } = await axios.post(
            `${API}/watch/round/${roundId}/event?t=${token}`,
            { type: ev.type },
          );
          if (data && data.ok === false && data.hint) {
            setErr(data.hint);
            setTimeout(() => setErr(""), 2500);
          }
          q.shift();
          saveQueue(q);
          setPendingCount(q.length);
        } catch {
          // Network or server error — bail out; will retry on next drain.
          break;
        }
      }
      if (q.length === 0) {
        // Once everything's flushed, sync state from server.
        await refresh();
      }
    } finally {
      drainingRef.current = false;
    }
  };
  // Keep ref in sync so the motion handler can call the latest sendEvent
  sendEventRef.current = sendEvent;
  // Keep the armed flag in a ref so the devicemotion handler sees the
  // latest value without re-binding the listener.
  holeArmedRef.current = !!state?.hole_armed;

  // Auto-disable auto-swing on hole change (so transit-between-greens doesn't count)
  useEffect(() => {
    if (autoSwing) {
      setAutoSwing(false);
      setSwingHint("Auto-swing OFF (new hole)");
      setTimeout(() => setSwingHint(""), 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.hole_number]);

  // GPS auto-arm — if the player has walked to within ~30 yds of the card
  // distance to the pin (i.e. they're at the tee box), automatically tap
  // Start Hole for them after a 3-second dwell. Buzzes the watch on fire so
  // they know it happened. Skipped when GPS is unavailable.
  useEffect(() => {
    const clearDwell = () => {
      if (autoArmDwellRef.current) {
        clearTimeout(autoArmDwellRef.current);
        autoArmDwellRef.current = null;
      }
    };
    if (!autoArm) return clearDwell;
    if (!state || state.hole_armed) return clearDwell;
    if (state.distance_source !== "gps") return clearDwell;
    const dist = state.distance_yds;
    const card = state.hole_yards;
    if (dist == null || card == null) return clearDwell;
    // "At the tee" = within 30 yds of the card distance to the pin.
    if (Math.abs(dist - card) > 30) return clearDwell;
    if (autoArmDwellRef.current) return clearDwell; // already counting down
    autoArmDwellRef.current = setTimeout(() => {
      autoArmDwellRef.current = null;
      try {
        if (navigator.vibrate) navigator.vibrate([60, 40, 60]);
      } catch {}
      setSwingHint("● Auto-armed at tee");
      setTimeout(() => setSwingHint(""), 1800);
      sendEventRef.current?.("start_hole");
    }, 3000);
    return clearDwell;
  }, [autoArm, state?.distance_yds, state?.hole_yards, state?.hole_armed, state?.distance_source]);

  const onToggleAutoArm = () => {
    const next = !autoArm;
    setAutoArm(next);
    localStorage.setItem("fairwayWatchAutoArm", String(next));
    setSwingHint(next ? "Auto-arm ON" : "Auto-arm OFF");
    setTimeout(() => setSwingHint(""), 1500);
  };

  const doFinish = async () => {
    if (finishBusy) return;
    setFinishBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/watch/round/${roundId}/finish?t=${token}&auto_par_blanks=true`
      );
      if (navigator.vibrate) navigator.vibrate([120, 80, 120, 80, 200]);
      setSummary(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't finish round");
      setTimeout(() => setErr(""), 3000);
    } finally {
      setFinishBusy(false);
    }
  };

  // When the round auto-completes (user putts on last hole and all holes have
  // strokes), the next refresh will show completed:true. Hit /finish so we get
  // the summary payload + league info, instead of leaving the user staring at
  // the play view of a finished round.
  useEffect(() => {
    if (state?.completed && !summary && !finishBusy) {
      doFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.completed]);

  const onToggleAuto = async () => {
    if (autoSwing) {
      setAutoSwing(false);
      return;
    }
    if (typeof window !== "undefined" && !window.__fairwayWatchSwingWarned) {
      const ok = window.confirm(
        "Auto-detect counts any quick acceleration (35 m/s²+) as a shot — practice swings WILL count. " +
        "Best used right before you hit the ball. Turn on?"
      );
      if (!ok) return;
      window.__fairwayWatchSwingWarned = true;
    }
    // iOS-style permission (rare on watches but defensive)
    if (typeof window?.DeviceMotionEvent?.requestPermission === "function") {
      try {
        const res = await window.DeviceMotionEvent.requestPermission();
        if (res !== "granted") {
          setErr("Motion permission denied");
          return;
        }
      } catch {
        setErr("Motion permission failed");
        return;
      }
    }
    setAutoSwing(true);
  };

  // Track whether we've already shown the auto-install prompt this device.
  // PWA install prompts can only fire once per session, and pestering users
  // who've already dismissed once is bad UX. Use localStorage to remember.
  const tryAutoInstall = () => {
    try {
      if (isInstalled) return;
      if (localStorage.getItem("fairwayWatchAutoPromptShown") === "1") return;
      if (!installPromptRef.current) return;
      localStorage.setItem("fairwayWatchAutoPromptShown", "1");
      doInstall({ silent: true });
    } catch { /* ignore */ }
  };

  const doLogin = async () => {
    setLoginErr("");
    setLoginBusy(true);
    try {
      const { data } = await axios.post(`${API}/watch/login`, {
        email: loginEmail.trim().toLowerCase(),
        pin: loginPin.trim(),
      });
      localStorage.setItem("fairwayWatchEmail", loginEmail.trim().toLowerCase());
      localStorage.setItem("fairwayWatchPin", loginPin.trim()); // needed to start a new round from watch
      localStorage.setItem("fairwayWatchName", data.user_name || "");
      setLoggedInName(data.user_name || "");
      if (data.has_active_round) {
        localStorage.setItem("fairwayWatchRoundId", data.round_id);
        localStorage.setItem("fairwayWatchToken", data.watch_token);
        setRoundId(data.round_id);
        setToken(data.watch_token);
      } else {
        // No active round → trigger the course picker so they can start one
        // straight from the watch.
        await loadPickerCourses();
      }
      // The login button click is a fresh user gesture — perfect moment to
      // pop the "Add to Home Screen" install prompt so the user doesn't
      // have to hunt for the small link at the bottom of the page later.
      tryAutoInstall();
    } catch (e) {
      setLoginErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoginBusy(false);
    }
  };

  // One-tap re-login using the email + PIN already saved in localStorage.
  // Used after a "Round expired" bounce so the user can rejoin their active
  // round without re-typing their PIN on the tiny watch keyboard.
  const continueLastRound = async () => {
    const savedEmail = (localStorage.getItem("fairwayWatchEmail") || "").trim().toLowerCase();
    const savedPin = (localStorage.getItem("fairwayWatchPin") || "").trim();
    if (!savedEmail || !savedPin) return;
    setLoginErr("");
    setLoginBusy(true);
    try {
      const { data } = await axios.post(`${API}/watch/login`, {
        email: savedEmail,
        pin: savedPin,
      });
      setLoggedInName(data.user_name || loggedInName);
      if (data.has_active_round) {
        localStorage.setItem("fairwayWatchRoundId", data.round_id);
        localStorage.setItem("fairwayWatchToken", data.watch_token);
        setRoundId(data.round_id);
        setToken(data.watch_token);
      } else {
        setLoginErr("No active round — start a new one below");
        await loadPickerCourses();
      }
      tryAutoInstall();
    } catch (e) {
      setLoginErr(e?.response?.data?.detail || "Couldn't continue — log in again");
    } finally {
      setLoginBusy(false);
    }
  };

  const loadPickerCourses = async () => {
    setPickerBusy(true);
    setPickerErr("");
    try {
      const { data } = await axios.post(`${API}/watch/courses`, {
        email: (localStorage.getItem("fairwayWatchEmail") || loginEmail).trim().toLowerCase(),
        pin: (localStorage.getItem("fairwayWatchPin") || loginPin).trim(),
      });
      setPickerCourses(data.courses || []);
    } catch (e) {
      setPickerErr(e?.response?.data?.detail || "Couldn't load courses");
      setPickerCourses([]);
    } finally {
      setPickerBusy(false);
    }
  };

  // PWA auto-launch: when the watch app opens from its home-screen icon
  // (display-mode: standalone) AND saved credentials exist AND we're not
  // already in a round, immediately call `continueLastRound()` so the
  // watch face appears in one tap from the home icon — just like a
  // native golf app. Fires only once per mount via the ref guard so we
  // don't loop on re-renders.
  const autoLaunchedRef = useRef(false);
  const [splashing, setSplashing] = useState(false);
  useEffect(() => {
    if (autoLaunchedRef.current) return;
    if (roundId || token) return; // already paired — no auto-launch needed
    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    if (!standalone) return;
    const savedEmail = (localStorage.getItem("fairwayWatchEmail") || "").trim();
    const savedPin = (localStorage.getItem("fairwayWatchPin") || "").trim();
    if (!savedEmail || !savedPin) return;
    autoLaunchedRef.current = true;
    // Show the splash overlay immediately so the user sees the Fairway
    // brand mark instead of a flash of the login form during the ~300-600ms
    // it takes to hit /watch/login and pair the watch token.
    setSplashing(true);
    const t = setTimeout(() => { continueLastRound(); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once the auto-launch resolves (roundId set → success, OR loginErr set
  // → fall through to login screen), dismiss the splash.
  useEffect(() => {
    if (!splashing) return;
    if (roundId || loginErr) {
      const t = setTimeout(() => setSplashing(false), 200);
      return () => clearTimeout(t);
    }
  }, [splashing, roundId, loginErr]);

  const startRound = async (courseId) => {
    setPickerBusy(true);
    setPickerErr("");
    try {
      const { data } = await axios.post(`${API}/watch/start-round`, {
        email: (localStorage.getItem("fairwayWatchEmail") || loginEmail).trim().toLowerCase(),
        pin: (localStorage.getItem("fairwayWatchPin") || loginPin).trim(),
        course_id: courseId,
      });
      localStorage.setItem("fairwayWatchRoundId", data.round_id);
      localStorage.setItem("fairwayWatchToken", data.watch_token);
      if (data.league_name) {
        localStorage.setItem("fairwayWatchLeagueName", data.league_name);
      } else {
        localStorage.removeItem("fairwayWatchLeagueName");
      }
      setRoundId(data.round_id);
      setToken(data.watch_token);
      setPickerCourses(null);
      setStarted(true);
    } catch (e) {
      setPickerErr(e?.response?.data?.detail || "Couldn't start round");
    } finally {
      setPickerBusy(false);
    }
  };

  const doLogout = () => {
    localStorage.removeItem("fairwayWatchRoundId");
    localStorage.removeItem("fairwayWatchToken");
    localStorage.removeItem("fairwayWatchPin");
    setRoundId("");
    setToken("");
    setStarted(false);
    setState(null);
    setPickerCourses(null);
  };

  if (splashing && !roundId) {
    // ============== SPLASH OVERLAY (PWA auto-launch handoff) ==============
    // Briefly shown when the user opens the watch PWA from their home-screen
    // icon — covers the ~300-600ms gap between the icon tap and the watch
    // face appearing so they don't see a flash of the login form.
    return (
      <div
        style={{
          ...rootStyle,
          background: "#143D2F",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
        data-testid="watch-splash"
      >
        <div
          style={{
            width: "clamp(64px, 22vmin, 140px)",
            height: "clamp(64px, 22vmin, 140px)",
            borderRadius: "50%",
            background: "#FFE45E",
            color: "#143D2F",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "clamp(28px, 10vmin, 64px)",
            fontWeight: 900,
            boxShadow: "0 0 48px rgba(255,228,94,0.35)",
            animation: "fwSplashPulse 1.2s ease-in-out infinite",
          }}
          aria-label="Fairway"
        >
          ⛳
        </div>
        <div
          style={{
            fontSize: "clamp(13px, 3.4vmin, 22px)",
            letterSpacing: 3,
            fontWeight: 700,
            color: "#F2EEE7",
            textTransform: "uppercase",
            opacity: 0.85,
          }}
        >
          Fairway · Watch
        </div>
        <style>{`
          @keyframes fwSplashPulse {
            0%, 100% { transform: scale(1);   opacity: 1; }
            50%      { transform: scale(1.08); opacity: 0.92; }
          }
        `}</style>
      </div>
    );
  }

  if (!roundId || !token) {
    // ============== COURSE PICKER (logged-in, no active round) ==============
    if (pickerCourses !== null) {
      return (
        <div style={rootStyle} data-testid="watch-picker">
          <div style={{ textAlign: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, opacity: 0.65, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>
              Start a round
            </div>
            {loggedInName && (
              <div style={{ fontSize: 10, opacity: 0.55, marginTop: 2 }}>as {loggedInName}</div>
            )}
          </div>
          {pickerBusy && <p style={{ fontSize: 12, opacity: 0.7, textAlign: "center" }}>…</p>}
          {!pickerBusy && pickerCourses.length === 0 && (
            <p style={{ fontSize: 11, opacity: 0.7, textAlign: "center", lineHeight: 1.5 }}>
              No courses found. Open the phone app and add a favourite first.
            </p>
          )}
          <div style={{ overflowY: "auto", maxHeight: "70vh", marginTop: 4 }}>
            {pickerCourses.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={pickerBusy}
                onClick={() => startRound(c.id)}
                data-testid={`watch-picker-course-${c.id}`}
                style={{
                  ...primaryBtnStyle,
                  fontSize: 15,
                  padding: "10px 12px",
                  textAlign: "left",
                  lineHeight: 1.2,
                }}
              >
                <div style={{ fontWeight: 900 }}>{c.name}</div>
                {c.location && (
                  <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7 }}>{c.location}</div>
                )}
              </button>
            ))}
          </div>
          {pickerErr && (
            <p data-testid="watch-picker-error" style={{ fontSize: 11, color: "#fda4af", textAlign: "center", marginTop: 6 }}>
              {pickerErr}
            </p>
          )}
          <button
            type="button"
            onClick={doLogout}
            data-testid="watch-picker-back"
            style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 11, marginTop: 8, textDecoration: "underline" }}
          >
            ← Log out
          </button>
        </div>
      );
    }

    return (
      <div style={rootStyle} data-testid="watch-login">
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 11, opacity: 0.65, letterSpacing: 2.5, textTransform: "uppercase", fontWeight: 700 }}>
            Fairway · Watch
          </div>
        </div>
        {/* One-tap "Continue last round" shortcut — shown when we have a
            saved email + PIN in localStorage (i.e. the user has logged in
            on this watch before). Avoids re-typing PIN on the tiny watch
            keyboard after a Round-expired bounce. */}
        {localStorage.getItem("fairwayWatchEmail") && localStorage.getItem("fairwayWatchPin") && (
          <button
            type="button"
            onClick={continueLastRound}
            disabled={loginBusy}
            data-testid="watch-continue-round"
            style={{
              ...primaryBtnStyle,
              background: "#FFE45E",
              color: "#143D2F",
              fontSize: 16,
              padding: "12px 16px",
              marginBottom: 12,
            }}
          >
            {loginBusy ? "…" : `Continue as ${(localStorage.getItem("fairwayWatchName") || "you").split(" ")[0]} ›`}
          </button>
        )}
        <input
          type="email"
          placeholder="email"
          value={loginEmail}
          onChange={(e) => setLoginEmail(e.target.value)}
          data-testid="watch-login-email"
          style={{
            ...inputStyle,
            marginBottom: 6,
          }}
          autoComplete="email"
        />
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="4-digit PIN"
          maxLength={8}
          value={loginPin}
          onChange={(e) => setLoginPin(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") doLogin(); }}
          data-testid="watch-login-pin"
          style={{ ...inputStyle, marginBottom: 10, textAlign: "center", letterSpacing: 6, fontSize: 22 }}
        />
        <button
          type="button"
          onClick={doLogin}
          disabled={loginBusy || !loginEmail || loginPin.length < 4}
          data-testid="watch-login-submit"
          style={{ ...primaryBtnStyle, fontSize: 20, padding: "12px 16px" }}
        >
          {loginBusy ? "…" : "Log in"}
        </button>
        {loginErr && (
          <p data-testid="watch-login-error" style={{ fontSize: 11, color: "#fda4af", textAlign: "center", marginTop: 8 }}>
            {loginErr}
          </p>
        )}
        <p style={{ fontSize: 9, opacity: 0.5, marginTop: 10, textAlign: "center", lineHeight: 1.4 }}>
          Set your PIN in the phone app first:<br />Profile → Watch PIN
        </p>
        <button
          type="button"
          onClick={async () => {
            // Hard-purge every cache + unregister every service worker, then
            // hard-reload. This is the "I can't see the new design" rescue
            // button — useful when a watch has held onto an old build.
            try {
              if ("serviceWorker" in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
              }
              if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
              }
            } catch {}
            window.location.reload(true);
          }}
          data-testid="watch-force-refresh"
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.4)",
            fontSize: 9,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            fontWeight: 700,
            textDecoration: "underline",
            marginTop: 6,
            padding: 4,
            cursor: "pointer",
          }}
        >
          Force refresh
        </button>

        {/* PWA install — adds Fairway to the watch home screen so users
            don't have to open the browser every round. */}
        {!isInstalled && (
          <button
            type="button"
            onClick={doInstall}
            data-testid="watch-install"
            style={{
              ...primaryBtnStyle,
              marginTop: 10,
              fontSize: 13,
              padding: "10px 12px",
              background: "rgba(255,228,94,0.15)",
              border: "1.5px solid #FFE45E",
              color: "#FFE45E",
              boxShadow: "none",
              fontWeight: 800,
            }}
          >
            ⤓ Add to home screen
          </button>
        )}
        {isInstalled && (
          <div
            style={{
              marginTop: 10,
              fontSize: 10,
              opacity: 0.55,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              textAlign: "center",
            }}
          >
            ✓ Installed on home screen
          </div>
        )}
        {showInstallHelp && (
          <div
            data-testid="watch-install-help"
            style={{
              marginTop: 10,
              fontSize: 13,
              lineHeight: 1.55,
              color: "#fff",
              background: "rgba(255,228,94,0.14)",
              border: "1px solid rgba(255,228,94,0.5)",
              borderRadius: 12,
              padding: "12px 14px",
              textAlign: "left",
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 8, color: "#FFE45E", fontSize: 14, letterSpacing: 0.5 }}>
              Install Fairway on your watch
            </div>
            <InstallSteps />
            <button
              type="button"
              onClick={() => {
                setShowInstallHelp(false);
                try { localStorage.setItem("fairwayWatchInstallHelpSeen", "1"); } catch { /* ignore */ }
              }}
              data-testid="watch-install-help-close"
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,0.65)",
                fontSize: 12,
                textDecoration: "underline",
                marginTop: 8,
                padding: 0,
                cursor: "pointer",
              }}
            >
              Got it
            </button>
          </div>
        )}
      </div>
    );
  }

  // ============== START SCREEN ==============
  if (!started) {
    return (
      <div style={rootStyle} data-testid="watch-start-screen">
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 13, opacity: 0.7, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>
            Fairway · Watch
          </div>
          {loggedInName && (
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>Hi {loggedInName} ⛳</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => { setStarted(true); tryAutoInstall(); }}
          data-testid="watch-start"
          style={{ ...primaryBtnStyle, fontSize: 28, padding: "20px 24px" }}
        >
          ▶ Start
        </button>
        <button
          type="button"
          onClick={doLogout}
          data-testid="watch-logout"
          style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.45)", fontSize: 10, marginTop: 12, textDecoration: "underline" }}
        >
          Log out
        </button>
        <p style={{ fontSize: 10, opacity: 0.5, marginTop: 4, textAlign: "center" }}>
          Make sure GPS / location is allowed
        </p>
      </div>
    );
  }

  if (!state) {
    return (
      <div style={{ ...rootStyle, justifyContent: "center" }} data-testid="watch-loading">
        <p style={{ fontSize: 14, opacity: 0.8 }}>{err || "Loading…"}</p>
      </div>
    );
  }

  // ============== ROUND COMPLETE / SUMMARY ==============
  if (summary || state.completed) {
    const totalStrokes = summary?.total_strokes ?? state.total_strokes;
    const totalPar = summary?.total_par ?? state.total_par;
    const vsPar = (summary?.vs_par ?? totalStrokes - totalPar) || 0;
    const leagueName = summary?.league_name || localStorage.getItem("fairwayWatchLeagueName") || null;
    const autoParred = summary?.holes_auto_parred || 0;
    const vsColor = vsPar > 0 ? "#fda4af" : vsPar < 0 ? "#86efac" : "#FFE45E";
    return (
      <div style={rootStyle} data-testid="watch-complete">
        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>
            Round done
          </div>
          <div style={{ fontSize: 80, fontWeight: 900, lineHeight: 0.9, marginTop: 10, letterSpacing: -3 }}>
            {totalStrokes}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              marginTop: 6,
              color: vsColor,
              letterSpacing: 1,
            }}
            data-testid="watch-complete-vs-par"
          >
            {vsPar > 0 ? `+${vsPar}` : vsPar === 0 ? "EVEN PAR" : `${vsPar}`} vs par {totalPar}
          </div>
          {leagueName && (
            <div
              data-testid="watch-complete-league"
              style={{
                marginTop: 16,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(34,197,94,0.18)",
                border: "1px solid rgba(34,197,94,0.5)",
                color: "#86efac",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                padding: "5px 10px",
                borderRadius: 9999,
              }}
            >
              ✓ Posted to {leagueName}
            </div>
          )}
          {!leagueName && summary && (
            <div
              style={{
                marginTop: 16,
                fontSize: 9,
                opacity: 0.55,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              No league attached
            </div>
          )}
          {autoParred > 0 && (
            <div
              style={{
                marginTop: 10,
                fontSize: 9,
                opacity: 0.55,
                fontWeight: 600,
                fontStyle: "italic",
              }}
            >
              {autoParred} hole{autoParred > 1 ? "s" : ""} auto-parred
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={doLogout}
          data-testid="watch-complete-done"
          style={{
            ...primaryBtnStyle,
            marginTop: 18,
            fontSize: 14,
            padding: "10px 12px",
            background: "transparent",
            border: "2px solid rgba(255,255,255,0.3)",
            color: "#fff",
          }}
        >
          ⌂ Home
        </button>
      </div>
    );
  }

  // ============== MAIN PLAY VIEW (Garmin-style watch face) ==============
  // Design intent: the screen IS a watch. No rectangular cards, no full-width
  // CTAs. A single round bezel fills the viewport, four cardinal-point pills
  // sit outside it (mimicking the four physical buttons on a Garmin Approach
  // S70), a long-press on the face opens the secondary menu (auto-arm, prev
  // hole, finish round). All status pills collapse into tiny dots inside the
  // face to keep the look clean.
  const dist = state.distance_yds ?? "—";
  const gpsLive = state.distance_source === "gps";
  const isLastHole = state.hole_number >= state.hole_total;
  const fmb = (pos && state.hole_lat != null && state.hole_lng != null)
    ? frontMiddleBack(pos.lat, pos.lng, state.hole_lat, state.hole_lng)
    : null;
  const middleYds = fmb?.middle ?? (typeof dist === "number" ? dist : null);

  // Build the 1–18 tick ring once per render.
  const ringTicks = [];
  for (let i = 1; i <= state.hole_total; i++) {
    const angle = (i / state.hole_total) * 2 * Math.PI - Math.PI / 2;
    ringTicks.push({ n: i, angle });
  }

  return (
    <div style={watchRootStyle} data-testid="watch-play">
      <style>{`
        @keyframes fairwayPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,228,94,0.5); }
          50%      { box-shadow: 0 0 0 10px rgba(255,228,94,0); }
        }
        @keyframes faceArm {
          0%   { box-shadow: inset 0 0 0 2px rgba(34,197,94,0.0); }
          50%  { box-shadow: inset 0 0 0 2px rgba(34,197,94,0.7); }
          100% { box-shadow: inset 0 0 0 2px rgba(34,197,94,0.0); }
        }
      `}</style>

      {/* The watch face. Sized to fit the smaller of viewport width/height so
          it stays a perfect circle regardless of device. */}
      <div
        style={watchFaceStyle(state.hole_armed)}
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="watch-face"
      >
        {/* Inner yardage-scale ring — Garmin-style ticks (50/100/150/200/250)
            sitting just inside the bezel. Purely decorative scale that
            communicates "this is a yardage device". */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 220 220"
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          {/* Major tick labels at the cardinal points (50, 100, 150, 200, 250).
              Positioned just inside the inner bezel edge. */}
          {[
            { yd: 250, angle: -Math.PI / 2 },           // top
            { yd: 50,  angle:  Math.PI / 2 },           // bottom
            { yd: 100, angle:  Math.PI / 2 - 0.6 },     // bottom-right
            { yd: 150, angle: -Math.PI / 4 },           // top-right
            { yd: 200, angle: -Math.PI / 2 + 0.35 },    // upper-top-right
          ].map(({ yd, angle }) => {
            const r = 96;
            const x = 110 + r * Math.cos(angle);
            const y = 110 + r * Math.sin(angle);
            return (
              <text
                key={yd}
                x={x}
                y={y}
                fontSize={6}
                fontWeight={700}
                fill="rgba(255,255,255,0.32)"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {yd}
              </text>
            );
          })}
          {/* Minor tick marks all the way round — every 15° = ~25 yd step */}
          {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i / 24) * 2 * Math.PI - Math.PI / 2;
            const r1 = 102, r2 = 105;
            const x1 = 110 + r1 * Math.cos(angle);
            const y1 = 110 + r1 * Math.sin(angle);
            const x2 = 110 + r2 * Math.cos(angle);
            const y2 = 110 + r2 * Math.sin(angle);
            return (
              <line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="rgba(255,255,255,0.18)"
                strokeWidth={i % 6 === 0 ? 1.4 : 0.7}
              />
            );
          })}
        </svg>

        {/* Top header — hole + par */}
        <div style={faceHeaderStyle} data-testid="watch-face-hole">
          #{state.hole_number} · Par {state.hole_par}
        </div>

        {/* Body — 3 cleanly-spaced columns:
              [chips: wind + elevation]  [yardages 55/41/27]  [mini hole map]
            Each column has its own breathing room — no overlap with the
            hole-number ring, the score chips, or each other. */}
        <div style={faceBodyStyle}>
          {/* Left column: stacked wind + elevation chips. Render the wind
              chip inline (not absolute) so it joins the flex flow. */}
          <div style={faceChipsColStyle}>
            <WatchWindChip
              playerLat={pos?.lat}
              playerLng={pos?.lng}
              pinLat={state.hole_lat}
              pinLng={state.hole_lng}
              onWind={setWind}
              inline
            />
            {state.elevation_adj && Math.abs(state.elevation_adj.delta_yds || 0) >= 3 && (
              <div
                data-testid="watch-elev-chip"
                title={`${state.elevation_adj.delta_m}m elevation — plays ${state.elevation_adj.delta_yds > 0 ? "+" : ""}${state.elevation_adj.delta_yds}yds`}
                style={faceElevChipStyle(state.elevation_adj.delta_yds)}
              >
                <span style={{ fontWeight: 900, fontSize: "1.05em" }}>
                  {state.elevation_adj.delta_yds > 0 ? "↑" : "↓"}
                </span>
                <span>
                  {state.elevation_adj.delta_yds > 0 ? "+" : ""}{state.elevation_adj.delta_yds}
                </span>
                <span style={{ fontSize: "0.65em", opacity: 0.75 }}>YDS</span>
              </div>
            )}
            {!online && (
              <div
                data-testid="watch-offline-chip"
                title="No signal — distances may be a few seconds stale. Scores are saved locally and will sync when you're back online."
                style={faceOfflineChipStyle}
              >
                <span style={{ fontWeight: 900 }}>⚠</span>
                <span>OFFLINE</span>
              </div>
            )}
          </div>

          <div style={faceLeftStackStyle}>
            {fmb ? (
              <>
                <div style={faceSideYdsStyle} data-testid="watch-face-back">{fmb.back}</div>
                <div style={faceMiddleStyle} data-testid="watch-face-middle">{fmb.middle}</div>
                {wind && wind.deltaYds !== 0 && fmb.middle != null && (
                  <div
                    data-testid="watch-face-wind-adj"
                    title={`${wind.mph}mph ${wind.cls === "head" ? "headwind" : "tailwind"} — play it as ${fmb.middle + wind.deltaYds}`}
                    style={faceWindAdjStyle(wind.cls)}
                  >
                    {wind.cls === "head" ? "+" : ""}{wind.deltaYds} · play {fmb.middle + wind.deltaYds}
                  </div>
                )}
                <div style={faceSideYdsStyle} data-testid="watch-face-front">{fmb.front}</div>
              </>
            ) : (
              <>
                <div style={faceMiddleStyle} data-testid="watch-face-middle">{middleYds ?? "—"}</div>
                {wind && wind.deltaYds !== 0 && middleYds != null && (
                  <div
                    data-testid="watch-face-wind-adj"
                    title={`${wind.mph}mph ${wind.cls === "head" ? "headwind" : "tailwind"} — play it as ${middleYds + wind.deltaYds}`}
                    style={faceWindAdjStyle(wind.cls)}
                  >
                    {wind.cls === "head" ? "+" : ""}{wind.deltaYds} · play {middleYds + wind.deltaYds}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mini hole map — fairway / green / pin / player position + real OSM hazards */}
          <MiniHoleMap
            holeYards={state.hole_yards}
            distanceToPin={middleYds}
            par={state.hole_par}
            armed={state.hole_armed}
            hazards={state.hazards}
          />
        </div>

        {/* Walker + walked-yards indicator below the body */}
        {middleYds != null && state.hole_yards && (
          <div style={faceWalkerStyle} data-testid="watch-face-walker">
            <span style={{ fontSize: "clamp(20px, 5vmin, 32px)" }}>🚶</span>
            <span style={{ fontSize: "clamp(20px, 5vmin, 36px)", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
              {Math.max(0, state.hole_yards - middleYds)}
            </span>
            <span style={{ fontSize: "clamp(11px, 2.6vmin, 16px)", opacity: 0.7, marginLeft: 2 }}>YDS</span>
          </div>
        )}

        {/* Score chips — small, sit in the lower-right of the face */}
        <div style={faceScoreChipsStyle}>
          <span data-testid="watch-face-shots" style={faceScorePillStyle}>
            <span style={{ opacity: 0.6, fontSize: "clamp(11px, 2.6vmin, 16px)", marginRight: 4 }}>S</span>
            {state.strokes ?? 0}
          </span>
          <span
            title={
              state.position_source === "phone"
                ? "GPS via host phone (saves watch battery)"
                : (gpsLive ? "GPS live (watch)" : "Card distance — GPS not locked")
            }
            style={{
              width: "clamp(10px, 2.4vmin, 16px)", height: "clamp(10px, 2.4vmin, 16px)", borderRadius: "50%",
              background: state.position_source === "phone"
                ? "#60a5fa"
                : (gpsLive ? "#22c55e" : "#fbbf24"),
              boxShadow: state.position_source === "phone"
                ? "0 0 6px rgba(96,165,250,0.7)"
                : (gpsLive ? "0 0 6px rgba(34,197,94,0.7)" : "none"),
            }}
          />
          <span data-testid="watch-face-putts" style={faceScorePillStyle}>
            <span style={{ opacity: 0.6, fontSize: "clamp(11px, 2.6vmin, 16px)", marginRight: 4 }}>P</span>
            {state.putts ?? 0}
          </span>
        </div>

        {/* Tiny pending-sync chip — fades in only when there's offline queue */}
        {pendingCount > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); drainQueue(); }}
            data-testid="watch-queue-pill"
            style={faceSyncChipStyle}
            title="Tap to sync now"
          >
            ⇡ {pendingCount}
          </button>
        )}

        {/* Crowdsourced pin-confirm pill — only shows when player is within
            30 yds of the pin, so it stays out of the way for most of a hole
            and only surfaces exactly when it's actionable (standing on the
            green). Tapping POSTs to /watch/round/{id}/confirm-pin. */}
        {middleYds != null && middleYds <= 30 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); confirmPin(); }}
            disabled={pinConfirm.busy}
            data-testid="watch-confirm-pin"
            aria-label="Confirm pin location"
            style={{
              position: "absolute",
              top: "clamp(24px, 6vmin, 42px)",
              left: "50%",
              transform: "translateX(-50%)",
              padding: "clamp(4px, 1.1vmin, 8px) clamp(10px, 2.6vmin, 16px)",
              background: pinConfirm.ack
                ? "rgba(34,197,94,0.85)"
                : pinConfirm.err
                  ? "rgba(196,84,60,0.9)"
                  : "rgba(255,228,94,0.95)",
              color: pinConfirm.ack || pinConfirm.err ? "#fff" : "#0b1512",
              border: "none",
              borderRadius: 999,
              fontSize: "clamp(10px, 2.4vmin, 14px)",
              fontWeight: 800,
              letterSpacing: ".02em",
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
              cursor: pinConfirm.busy ? "wait" : "pointer",
              opacity: pinConfirm.busy ? 0.75 : 1,
              whiteSpace: "nowrap",
              zIndex: 5,
              transition: "background .25s ease, color .25s ease",
            }}
          >
            {pinConfirm.busy
              ? "…"
              : pinConfirm.ack
                ? pinConfirm.ack
                : pinConfirm.err
                  ? pinConfirm.err
                  : "📍 Confirm pin"}
          </button>
        )}
      </div>

      {/* OUTER hole-number ring — sits outside the bezel, orbiting between
          the bezel edge and the cardinal action buttons. Mimics the Garmin
          S70's bezel-printed hole numbers. Current hole = Fairway yellow. */}
      <svg
        style={outerRingSvgStyle}
        viewBox="0 0 300 300"
        aria-hidden="true"
      >
        {ringTicks.map(({ n, angle }) => {
          const r = 142; // just outside the face (face = ~129 radius at 86vw on screen)
          const x = 150 + r * Math.cos(angle);
          const y = 150 + r * Math.sin(angle);
          const active = n === state.hole_number;
          return (
            <text
              key={n}
              x={x}
              y={y}
              fontSize={active ? 20 : 13}
              fontWeight={active ? 900 : 700}
              fill={active ? "#FFE45E" : "rgba(255,255,255,0.65)"}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {n}
            </text>
          );
        })}
      </svg>

      {/* Four cardinal "physical buttons" around the bezel.
          - TOP   = Start Hole (when un-armed) / Putt (when armed)
          - RIGHT = + Shot
          - LEFT  = − Shot (undo)
          - BOTTOM = Menu (opens secondary actions) */}
      <button
        type="button"
        onClick={() => {
          if (state.hole_armed) sendEvent("putt");
          else sendEvent("start_hole");
        }}
        disabled={busy}
        data-testid={state.hole_armed ? "watch-putt" : "watch-start-hole"}
        style={{
          ...watchButtonStyle("top"),
          background: state.hole_armed ? "#FFE45E" : "#22c55e",
          color: state.hole_armed ? "#143D2F" : "#fff",
          animation: state.hole_armed ? "none" : "fairwayPulse 2s ease-in-out infinite",
        }}
        title={state.hole_armed ? (isLastHole ? "Finish round" : "Putt / next hole") : "Start hole"}
      >
        {state.hole_armed ? (isLastHole ? "FIN" : "PUT") : "GO"}
      </button>

      <button
        type="button"
        onClick={() => sendEvent("stroke")}
        disabled={busy || !state.hole_armed}
        data-testid="watch-stroke"
        style={{ ...watchButtonStyle("right"), opacity: state.hole_armed ? 1 : 0.35 }}
        title="Add a stroke"
      >
        +
      </button>

      <button
        type="button"
        onClick={() => sendEvent("undo_stroke")}
        disabled={busy || !(state.strokes > 0)}
        data-testid="watch-undo-shot"
        style={{ ...watchButtonStyle("left"), opacity: state.strokes > 0 ? 1 : 0.35 }}
        title="Undo last stroke"
      >
        −
      </button>

      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        data-testid="watch-menu"
        style={watchButtonStyle("bottom")}
        title="Menu"
        aria-label="Menu"
      >
        ☰
      </button>

      {/* Secondary action sheet — only shown when user taps the face / menu
          button. Slim list, no big cards. Mimics the Garmin button menu. */}
      {menuOpen && (
        <div
          data-testid="watch-menu-sheet"
          onClick={() => setMenuOpen(false)}
          style={watchMenuOverlayStyle}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={watchMenuPanelStyle}
          >
            <div style={watchMenuTitleStyle}>
              Hole {state.hole_number}/{state.hole_total} · Par {state.hole_par}
            </div>
            {/* Explicit "Back to watch" — tapping the dim overlay also
                closes the menu, but on a small watch screen that target is
                hard to hit. Users were getting stuck thinking the menu was
                a separate page they had to log out of. */}
            <MenuRow
              label="← Back to watch"
              tone="muted"
              onClick={() => setMenuOpen(false)}
              data-testid="watch-menu-back"
            />
            <MenuRow
              label={state.hole_armed ? "Hole started ✓" : "Start this hole"}
              onClick={() => { if (!state.hole_armed) sendEvent("start_hole"); setMenuOpen(false); }}
              disabled={state.hole_armed}
            />
            <MenuRow
              label="Drop ball (+1 penalty)"
              onClick={() => { sendEvent("stroke"); setMenuOpen(false); }}
              disabled={!state.hole_armed}
            />
            <MenuRow
              label={`Auto-swing · ${autoSwing ? "ON" : "OFF"}`}
              onClick={onToggleAuto}
            />
            <MenuRow
              label={`Auto-arm at tee · ${autoArm ? "ON" : "OFF"}`}
              onClick={onToggleAutoArm}
            />
            <MenuRow
              label="← Previous hole"
              onClick={() => { sendEvent("prev"); setMenuOpen(false); }}
              disabled={state.hole_number <= 1}
            />
            <MenuRow
              label="Finish round now"
              tone="danger"
              onClick={() => {
                setMenuOpen(false);
                if (window.confirm("Finish the round now? Any holes you haven't played will be auto-parred.")) {
                  doFinish();
                }
              }}
            />
            <MenuRow
              label="Back to home"
              tone="muted"
              onClick={() => {
                setMenuOpen(false);
                if (window.confirm("Back to home? Your round stays active on the phone.")) {
                  localStorage.removeItem("fairwayWatchRoundId");
                  localStorage.removeItem("fairwayWatchToken");
                  setRoundId("");
                  setToken("");
                  setStarted(false);
                  setState(null);
                  loadPickerCourses();
                }
              }}
            />
          </div>
        </div>
      )}

      {swingHint && (
        <div data-testid="watch-swing-hint" style={watchHintStyle}>
          {swingHint}
        </div>
      )}
      {err && (
        <div data-testid="watch-error" style={{ ...watchHintStyle, color: "#fda4af" }}>
          {err}
        </div>
      )}
    </div>
  );
}

/** Slim row in the secondary menu — Garmin button menu vibe. */
function InstallSteps() {
  // Detect the user's browser from UA so we show ONE clear set of steps,
  // not a wall of generic instructions covering every device on Earth.
  // Watch browsers vary wildly — Galaxy Watch (Samsung Internet) needs a
  // different tap path to Apple Watch (Safari) or Wear OS (Chrome).
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isSamsung = /SamsungBrowser/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  const isChromeAndroid = /Android.*Chrome/i.test(ua);
  const isFirefox = /Firefox|FxiOS/i.test(ua);
  let steps;
  if (isSamsung) {
    steps = [
      'Tap the ☰ or ⋮ menu at the top of this page.',
      'Tap "Add page to" → "Home screen".',
      'Confirm "Add" — Fairway lands on your watch home screen.',
    ];
  } else if (isIOS) {
    steps = [
      'Tap the Share icon (square with up-arrow) in Safari.',
      'Scroll down → "Add to Home Screen".',
      'Tap "Add" in the top-right.',
    ];
  } else if (isChromeAndroid) {
    steps = [
      'Tap the ⋮ menu (top-right).',
      'Tap "Install app" or "Add to Home Screen".',
      'Confirm "Install" — opens like a native app.',
    ];
  } else if (isFirefox) {
    steps = [
      'Tap the ⋮ menu.',
      'Tap "Install" (or "Add to Home Screen").',
      'Confirm — shortcut lands on your home screen.',
    ];
  } else {
    steps = [
      'Open your browser menu (⋮ or ☰).',
      'Look for "Install app", "Add to Home Screen", or "Add page to home screen".',
      'Tap it and confirm.',
    ];
  }
  return (
    <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
      {steps.map((s, i) => (
        <li key={i} style={{ fontSize: 13, lineHeight: 1.45 }}>{s}</li>
      ))}
    </ol>
  );
}


function MenuRow({ label, onClick, disabled, tone, "data-testid": dataTestId }) {
  const color =
    tone === "danger" ? "#fda4af" :
    tone === "muted"  ? "rgba(255,255,255,0.55)" :
    "#fff";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      style={{
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        color,
        opacity: disabled ? 0.35 : 1,
        padding: "12px 14px",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.3,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );
}

/**
 * MiniHoleMap — schematic of the current hole, Garmin S70 style.
 * Drawn purely from the hole length + remaining distance (no per-hole shape
 * data needed). Renders a vertical "fairway corridor" with a green at the
 * top (3 colored markers for B/M/F), a tee at the bottom, and a player
 * marker that walks up the fairway as the user's GPS distance to pin drops.
 * If we know the par we curve the corridor — par 5 = dogleg right, par 3 =
 * very short, par 4 = straight — to give each hole a unique silhouette.
 */
function MiniHoleMap({ holeYards, distanceToPin, par, armed, hazards }) {
  // Where along the corridor is the user? 0 = tee (bottom), 1 = pin (top).
  const progress = (holeYards && distanceToPin != null && distanceToPin <= holeYards)
    ? Math.max(0, Math.min(1, 1 - distanceToPin / holeYards))
    : 0;
  // SVG canvas. Made tall + narrow because the map sits to the right of
  // the yardage stack and we want the green at the top to draw the eye.
  const W = 78;
  const H = 168;
  // Fairway centre-line — a quadratic curve from tee (bottom) to green (top).
  // Direction of curve = "shape" of the hole. Picks a sensible default by par.
  const curveX = par === 3 ? 0 : par === 5 ? 18 : -10;
  const teeX = W / 2;
  const teeY = H - 12;
  const greenX = W / 2 + (par === 5 ? 8 : par === 3 ? -2 : 0);
  const greenY = 28;
  const ctrlX = (teeX + greenX) / 2 + curveX;
  const ctrlY = (teeY + greenY) / 2;
  // Quadratic Bezier evaluator — used for both player position and hazard
  // projection (hazards lie roughly along the same play-line curve).
  const bez = (t) => ({
    x: (1 - t) * (1 - t) * teeX + 2 * (1 - t) * t * ctrlX + t * t * greenX,
    y: (1 - t) * (1 - t) * teeY + 2 * (1 - t) * t * ctrlY + t * t * greenY,
  });
  const playerP = bez(progress);

  // Visualise hazards from OSM. Each hazard has dist_from_pin_yds (0 = green,
  // hole_yards = tee) and lateral_yds (+ve right, -ve left). We map those into
  // local SVG coords by:
  //   t (along curve)  = 1 - dist_from_pin_yds / hole_yards
  //   normal direction = perpendicular to the curve tangent at that point
  //   px lateral       = lateral_yds * 0.45  (rough cap so map doesn't overflow)
  function plotHazard(h, i) {
    if (!holeYards) return null;
    const t = Math.max(0.02, Math.min(0.98, 1 - h.dist_from_pin_yds / holeYards));
    const p = bez(t);
    // Tangent at t (derivative of quadratic Bezier).
    const tx = 2 * (1 - t) * (ctrlX - teeX) + 2 * t * (greenX - ctrlX);
    const ty = 2 * (1 - t) * (ctrlY - teeY) + 2 * t * (greenY - ctrlY);
    const nMag = Math.hypot(tx, ty) || 1;
    const nx = -ty / nMag;
    const ny = tx / nMag;
    const lat = Math.max(-22, Math.min(22, h.lateral_yds * 0.45));
    const cx = p.x + nx * lat;
    const cy = p.y + ny * lat;
    const sz = Math.max(2.5, Math.min(6, (h.size_yds || 6) * 0.35));
    if (h.kind === "bunker") {
      return <ellipse key={`b${i}`} cx={cx} cy={cy} rx={sz} ry={sz * 0.7} fill="#d9c98a" stroke="#a98f56" strokeWidth="0.5" />;
    }
    if (h.kind === "water") {
      return <ellipse key={`w${i}`} cx={cx} cy={cy} rx={sz * 1.1} ry={sz * 0.6} fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.5" opacity="0.85" />;
    }
    // green/fairway features handled by plotOutline (uses real polygon)
    return null;
  }

  // Project a (dist_from_pin_yds, lateral_yds) corridor point into local SVG
  // coords using the same bezier + normal-offset mapping as plotHazard. Used
  // for rendering REAL fairway/green polygons from OSM data instead of the
  // schematic curve, giving the watch face an accurate hole-shape preview.
  function projectCorridor(distFromPin, lateralYds) {
    if (!holeYards) return null;
    const t = Math.max(0, Math.min(1, 1 - distFromPin / holeYards));
    const p = bez(t);
    const tx = 2 * (1 - t) * (ctrlX - teeX) + 2 * t * (greenX - ctrlX);
    const ty = 2 * (1 - t) * (ctrlY - teeY) + 2 * t * (greenY - ctrlY);
    const nMag = Math.hypot(tx, ty) || 1;
    const nx = -ty / nMag;
    const ny = tx / nMag;
    // Bigger lateral scaling for outlines so a 30-yard-wide fairway actually
    // looks like a fairway. Capped so it can't escape the SVG card.
    const lat = Math.max(-30, Math.min(30, lateralYds * 0.55));
    return { x: p.x + nx * lat, y: p.y + ny * lat };
  }

  // Polygon helper for fairway/green outlines from OSM.
  function plotOutline(h, i, fill, stroke) {
    if (!Array.isArray(h.outline) || h.outline.length < 3 || !holeYards) return null;
    const pts = h.outline
      .map(([d, l]) => projectCorridor(d, l))
      .filter(Boolean)
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(" ");
    if (!pts) return null;
    return (
      <polygon
        key={`o${i}`}
        points={pts}
        fill={fill}
        stroke={stroke}
        strokeWidth="0.8"
        strokeLinejoin="round"
        opacity="0.85"
      />
    );
  }

  // Pre-compute which OSM features have real outlines so we can swap the
  // schematic fairway curve OUT when real data is available.
  // We also apply a sanity filter — OSM near a course's lat/lng can return
  // greens/fairways that belong to OTHER holes (or even nearby parks). We
  // only trust an outline when:
  //   • green: centroid is within 25yds of the pin AND within 25yds laterally
  //   • fairway: centroid is within the hole corridor AND within 40yds laterally
  // Otherwise we fall back to the schematic, which looks better than a wrong
  // polygon floating off-target.
  const realFairways = Array.isArray(hazards)
    ? hazards.filter((h) =>
        h.kind === "fairway"
        && Array.isArray(h.outline) && h.outline.length >= 3
        && h.dist_from_pin_yds >= 0
        && (!holeYards || h.dist_from_pin_yds <= holeYards * 1.05)
        && Math.abs(h.lateral_yds) <= 40
      )
    : [];
  const realGreens = Array.isArray(hazards)
    ? hazards.filter((h) =>
        h.kind === "green"
        && Array.isArray(h.outline) && h.outline.length >= 3
        && h.dist_from_pin_yds <= 25
        && Math.abs(h.lateral_yds) <= 25
      )
    : [];
  const hasRealHoleShape = realFairways.length > 0 || realGreens.length > 0;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      style={{
        flexShrink: 0,
        width: "clamp(85px, 26vmin, 240px)",
        height: "clamp(160px, 42vmin, 420px)",
      }}
      role="img"
      aria-label="Hole map"
      data-testid="watch-mini-map"
    >
      {/* Subtle rounded card outline — gives the map a clean "card" feel
          so it reads as a distinct surface from the rest of the watch face. */}
      <rect x="1" y="1" width={W - 2} height={H - 2} rx="10" ry="10"
        fill="none" stroke="rgba(255,228,94,0.18)" strokeWidth="1" />

      {/* Real hole shape from OSM — fairway polygon(s) drawn FIRST so
          everything else (hazards, player, flag) sits on top. When no real
          outline is available the schematic bezier corridor below renders
          instead. */}
      {realFairways.map((h, i) => plotOutline(h, i, "#2d6a3f", "rgba(45,106,63,0.6)"))}

      {/* Fairway corridor — schematic bezier from tee to green. Only drawn
          when we don't have a real OSM fairway polygon for this hole. */}
      {!hasRealHoleShape && (
        <path
          d={`M ${teeX} ${teeY} Q ${ctrlX} ${ctrlY} ${greenX} ${greenY}`}
          stroke="#2d6a3f"
          strokeWidth="22"
          fill="none"
          strokeLinecap="round"
          opacity="0.55"
        />
      )}
      {/* Decorative trees — fallback only, hidden once we have real OSM data
          so they don't clash with the actual hole outline. */}
      {!hasRealHoleShape && [40, 70, 100, 130].map((y, i) => (
        <g key={`t${i}`}>
          <circle cx={i % 2 === 0 ? 8 : W - 8} cy={y} r="5" fill="#1d4a2b" opacity="0.7" />
          <circle cx={i % 2 === 0 ? 14 : W - 14} cy={y - 8} r="4" fill="#1d4a2b" opacity="0.55" />
        </g>
      ))}

      {/* Real hazards from OSM — bunkers + water rendered here so they sit
          ON the fairway corridor, not under it. */}
      {Array.isArray(hazards) && hazards.length > 0
        ? hazards.map((h, i) => plotHazard(h, i))
        : (par !== 3 && (
            // Schematic water-hint fallback for par 4/5 when we don't have
            // real OSM data yet — keeps the map from looking blank.
            <path
              d={`M ${W - 22} ${H * 0.55} q -6 6 0 12 q 6 6 0 12`}
              stroke="#2563eb"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              opacity="0.55"
            />
          ))
      }

      {/* Green — real OSM polygon when available, otherwise a schematic
          circle at the top of the corridor. The flag stick + B/M/F dots
          always render so the user has a clear pin target either way. */}
      {realGreens.length > 0
        ? realGreens.map((h, i) => plotOutline(h, `g${i}`, "#3f9a4f", "#ffffff"))
        : <circle cx={greenX} cy={greenY} r="14" fill="#3f9a4f" stroke="#fff" strokeWidth="1" />
      }
      <circle cx={greenX} cy={greenY + 9} r="2" fill="#ef4444" />
      <circle cx={greenX} cy={greenY}      r="2.5" fill="#FFE45E" />
      <circle cx={greenX} cy={greenY - 9} r="2" fill="#ffffff" />
      {/* Flag stick */}
      <line x1={greenX + 6} y1={greenY - 12} x2={greenX + 6} y2={greenY - 22}
        stroke="#ffffff" strokeWidth="1" />
      <polygon
        points={`${greenX + 6},${greenY - 22} ${greenX + 13},${greenY - 19} ${greenX + 6},${greenY - 17}`}
        fill="#ef4444"
      />

      {/* Tee marker at the bottom */}
      <rect x={teeX - 5} y={teeY - 1} width="10" height="3" rx="1.5" fill="rgba(255,255,255,0.45)" />

      {/* Player position — pulse if hole is armed, static otherwise */}
      <circle
        cx={playerP.x}
        cy={playerP.y}
        r={armed ? "6" : "5"}
        fill="#FFE45E"
        stroke="#0b1410"
        strokeWidth="1.5"
      >
        {armed && (
          <animate
            attributeName="r"
            values="5;7;5"
            dur="1.6s"
            repeatCount="indefinite"
          />
        )}
      </circle>
    </svg>
  );
}

// ---- Styles (inline so this page is self-contained and watch-browser safe) ----
const rootStyle = {
  background: "#143D2F",
  color: "#F2EEE7",
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  padding: 12,
  fontFamily: "'Inter', system-ui, sans-serif",
  boxSizing: "border-box",
};

// ===== Garmin-style play view styles =====
// The play view replaces the rectangular `rootStyle` with a full-bleed black
// background — the screen literally becomes a watch face.
const watchRootStyle = {
  background: "#000",
  color: "#fff",
  minHeight: "100vh",
  width: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Inter', system-ui, sans-serif",
  boxSizing: "border-box",
  overflow: "hidden",
};

// Round bezel — sized to the smaller of viewport width/height so it stays
// circular on any device (Galaxy Watch, phone in landscape, etc.).
const watchFaceStyle = (armed) => ({
  position: "relative",
  width: "min(86vw, 86vh)",
  height: "min(86vw, 86vh)",
  borderRadius: "50%",
  background: "radial-gradient(circle at 35% 30%, #1a2a22 0%, #050a07 75%)",
  boxShadow: armed
    ? "inset 0 0 0 2px rgba(34,197,94,0.55), 0 8px 32px rgba(0,0,0,0.55)"
    : "inset 0 0 0 2px rgba(255,228,94,0.35), 0 8px 32px rgba(0,0,0,0.55)",
  border: "8px solid #0b1410",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
});

const faceHeaderStyle = {
  position: "absolute",
  top: "10%",
  left: 0,
  right: 0,
  textAlign: "center",
  fontSize: "clamp(12px, 3.8vmin, 26px)",
  letterSpacing: 2,
  fontWeight: 800,
  color: "rgba(255,255,255,0.85)",
  textTransform: "uppercase",
};

const faceStackStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  lineHeight: 1,
  gap: 4,
  fontVariantNumeric: "tabular-nums",
};

// Body grid below the header — yardages left, hole-map right.
const faceBodyStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "clamp(6px, 2vmin, 14px)",
  paddingTop: "3%",
};

// Left column: stacked wind + elevation chips. Aligned to start of column
// so the chips don't expand to fill — keeps the column narrow.
const faceChipsColStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  flexShrink: 0,
};

const faceLeftStackStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  lineHeight: 1,
  gap: 2,
  fontVariantNumeric: "tabular-nums",
};

// Walker + yards-walked indicator, mirroring Garmin's golfer icon + carry
// distance below the body.
const faceWalkerStyle = {
  position: "absolute",
  bottom: "18%",
  left: "16%",
  display: "flex",
  alignItems: "center",
  gap: 4,
  color: "rgba(255,255,255,0.85)",
};

const faceScoreChipsStyle = {
  position: "absolute",
  bottom: "10%",
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
};

const faceMiddleStyle = {
  fontSize: "clamp(36px, 12vmin, 92px)",
  fontWeight: 900,
  color: "#FFE45E",
  letterSpacing: -2,
  lineHeight: 0.85,
};

const faceSideYdsStyle = {
  fontSize: "clamp(16px, 4.8vmin, 38px)",
  fontWeight: 800,
  color: "rgba(255,255,255,0.85)",
  lineHeight: 0.95,
};

// Wind-adjusted "play it as N" hint that sits between the middle headline
// yardage and the front yardage. Colour-coded:
//   • head → terracotta red (club up, play it longer)
//   • tail → green (club down, play it shorter)
const faceWindAdjStyle = (cls) => ({
  fontSize: "clamp(11px, 2.8vmin, 18px)",
  fontWeight: 800,
  letterSpacing: 0.4,
  color: cls === "head" ? "#fca5a5" : cls === "tail" ? "#86efac" : "rgba(255,255,255,0.7)",
  marginTop: 2,
  marginBottom: 2,
  fontVariantNumeric: "tabular-nums",
});

// Offline indicator chip — same column as wind + elevation, amber colour
// (warning, not error). Shown when navigator.onLine flips false; the watch
// keeps working from cached state + service worker, but the user should
// know their distances may lag and that scores queue locally until they're
// back online.
const faceOfflineChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  background: "rgba(251,191,36,0.18)",
  color: "#fcd34d",
  border: "1px solid rgba(251,191,36,0.55)",
  borderRadius: 9999,
  padding: "2px 8px",
  fontSize: "clamp(10px, 2.4vmin, 14px)",
  fontWeight: 800,
  letterSpacing: 0.6,
  pointerEvents: "none",
  backdropFilter: "blur(6px)",
};

// Elevation chip — sits in the left chips column alongside the wind chip.
// Colour-codes uphill (terracotta = harder, club up) vs downhill (green =
// easier, club down).
const faceElevChipStyle = (deltaYds) => {
  const uphill = deltaYds > 0;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: uphill ? "rgba(239,68,68,0.18)" : "rgba(34,197,94,0.18)",
    color: uphill ? "#fca5a5" : "#86efac",
    border: `1px solid ${uphill ? "rgba(239,68,68,0.55)" : "rgba(34,197,94,0.55)"}`,
    borderRadius: 9999,
    padding: "2px 8px",
    fontSize: "clamp(10px, 2.6vmin, 16px)",
    fontWeight: 800,
    letterSpacing: 0.3,
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
    backdropFilter: "blur(6px)",
  };
};

// Outer hole-number ring SVG — sized to the face plus a little padding so the
// numbers orbit outside the bezel ring. Sits behind the cardinal buttons.
const outerRingSvgStyle = {
  position: "absolute",
  width: "min(96vw, 96vh)",
  height: "min(96vw, 96vh)",
  pointerEvents: "none",
};

const faceFooterStyle = {
  position: "absolute",
  bottom: "14%",
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 14,
  fontSize: 12,
  fontWeight: 800,
  fontVariantNumeric: "tabular-nums",
};

const faceScorePillStyle = {
  display: "inline-flex",
  alignItems: "center",
  color: "rgba(255,255,255,0.95)",
  fontSize: "clamp(18px, 5vmin, 34px)",
  fontWeight: 800,
};

const faceSyncChipStyle = {
  position: "absolute",
  top: "8%",
  right: "10%",
  background: "rgba(251,191,36,0.18)",
  color: "#fbbf24",
  border: "1px solid rgba(251,191,36,0.5)",
  borderRadius: 9999,
  padding: "2px 7px",
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: 0.4,
  cursor: "pointer",
  fontFamily: "inherit",
};

// Cardinal-point pill buttons that orbit the bezel. Each position maps to
// a corner of a real Garmin's physical button cluster.
const watchButtonStyle = (pos) => {
  const base = {
    position: "absolute",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#fff",
    width: 56,
    height: 56,
    borderRadius: "50%",
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: 0.4,
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(6px)",
    WebkitTapHighlightColor: "transparent",
  };
  if (pos === "top")    return { ...base, top: "1%",  left: "50%", transform: "translateX(-50%)" };
  if (pos === "bottom") return { ...base, bottom: "1%", left: "50%", transform: "translateX(-50%)", fontSize: 18 };
  if (pos === "left")   return { ...base, left: "1%",  top: "50%", transform: "translateY(-50%)", fontSize: 28 };
  if (pos === "right")  return { ...base, right: "1%", top: "50%", transform: "translateY(-50%)", fontSize: 28 };
  return base;
};

const watchMenuOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  padding: 24,
};

const watchMenuPanelStyle = {
  width: "min(320px, 92vw)",
  background: "#0b1410",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
};

const watchMenuTitleStyle = {
  padding: "12px 14px 10px",
  fontSize: 10,
  letterSpacing: 1.8,
  textTransform: "uppercase",
  fontWeight: 800,
  color: "rgba(255,255,255,0.6)",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
};

const watchHintStyle = {
  position: "fixed",
  bottom: 12,
  left: 0,
  right: 0,
  textAlign: "center",
  fontSize: 10,
  color: "#22c55e",
  fontWeight: 700,
  letterSpacing: 0.5,
  pointerEvents: "none",
};

const primaryBtnStyle = {
  background: "#FFE45E",
  color: "#143D2F",
  border: "none",
  borderRadius: 14,
  padding: "14px 16px",
  fontSize: 22,
  fontWeight: 900,
  letterSpacing: 0.5,
  marginBottom: 6,
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
  fontFamily: "inherit",
};

const btnRowStyle = {
  display: "flex",
  gap: 6,
  marginBottom: 6,
};

const inputStyle = {
  background: "rgba(255,255,255,0.1)",
  color: "#F2EEE7",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 16,
  fontFamily: "inherit",
  outline: "none",  width: "100%",
  boxSizing: "border-box",
};

// Tiny helpers for the "synced Xs ago" pill — keep the play-view JSX clean.
function ageMs(ts) {
  return ts ? Date.now() - ts : Infinity;
}
function ageLabel(ts) {
  const sec = Math.max(0, Math.round((Date.now() - (ts || 0)) / 1000));
  if (sec < 5) return "synced";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

